import { useContext, useEffect, useState } from 'react';
import { AuthContext } from 'Contexts/AuthContext';
import { fetchGroup } from 'App/Group/api';
import { GroupSummary } from 'App/Group/types';

/**
 * The signed-in user's groups whose dashboards include the restaurant (not
 * deleted there). Unreachable groups are left out; another user or
 * restaurant shows nothing stale.
 */
const useRestaurantGroups = (restaurantId: string | null) => {
  const { user } = useContext(AuthContext);
  const groups = user?.data?.groups ?? [];
  const groupKey = JSON.stringify([user?.uid, groups.map((group) => group.id)]);
  const [memberships, setMemberships] = useState<{
    key: string;
    groups: GroupSummary[];
  }>({ key: '', groups: [] });

  useEffect(() => {
    let active = true;
    const [, groupIds] = JSON.parse(groupKey) as [string | null, string[]];
    Promise.allSettled(groupIds.map(fetchGroup)).then((results) => {
      if (!active) return;
      setMemberships({
        key: groupKey,
        groups: results.flatMap((result) =>
          result.status === 'fulfilled' ? [result.value] : [],
        ),
      });
    });
    return () => {
      active = false;
    };
  }, [groupKey]);

  return memberships.key === groupKey
    ? groups.filter((group) =>
        memberships.groups.some(
          (membership) =>
            membership.id === group.id &&
            membership.restaurants.some(
              (restaurant) =>
                restaurant.id === restaurantId && !restaurant.deleted,
            ),
        ),
      )
    : [];
};

export default useRestaurantGroups;
